import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from "react";
import { ChevronDown, Link2, Paperclip, PencilLine } from "lucide-react";
import { DEFAULT_TASK_PRIORITY, type TaskPriority, type TaskStatus } from "@quovy/types";

import { cn, filesFromDataTransfer } from "../../utils";
import { toast } from "../../feedback/toast";
import { Button } from "../../inputs/button";
import { Input } from "../../inputs/input";
import { MarkdownEditor } from "../../inputs/markdown-editor";
import { Textarea } from "../../inputs/textarea";
import { Label } from "../../primitives/label";
import { TaskTitleInput } from "./task-title-input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../primitives/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../primitives/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import { AssigneeSelector } from "./assignee-selector";
import { StatusSelector } from "./status-selector";
import { PrioritySelector } from "./priority-selector";
import { DueDateSelector } from "./due-date-selector";
import {
  initialTemplateValues,
  renderTemplateSegments,
  renderTemplateString,
  resolveDueOffset,
  templateFieldCountLabel,
  type TaskTemplate,
  type TaskTemplateField,
} from "./task-template";
import { mentionablesFromIdentities, type TaskIdentitySummary } from "./task-detail-view";

export interface PrimaryResourceRef {
  type: string;
  id: string;
  label: string;
}

/** A parent task the new task is being created as a sub-task of. Read-only in the form. */
export interface TaskParentRef {
  publicId: string;
  taskNumber: number | null;
  title: string;
}

export interface CreateTaskInput {
  title: string;
  description?: string;
  assignee?: TaskIdentitySummary | null;
  status?: TaskStatus;
  priority?: TaskPriority;
  dueDate?: number | null;
  primaryResource?: PrimaryResourceRef;
  parentTask?: TaskParentRef;
}

/**
 * Initial field values for the form — used by deep-link prefill (e.g. the
 * `/tasks/new?title=…&priority=high` route). Every field is optional; omitted
 * fields fall back to their empty/default state. Read once on mount and
 * re-applied by {@link CreateTaskFormHandle.reset}.
 */
export interface CreateTaskInitialValues {
  title?: string;
  description?: string;
  assignee?: TaskIdentitySummary | null;
  status?: TaskStatus;
  priority?: TaskPriority;
  dueDate?: number | null;
}

/**
 * Imperative handle for {@link CreateTaskForm}. The form owns its field state;
 * the surrounding chrome (a dialog footer, a page submit button) drives submit
 * and reset through this handle so the same form body backs every entry point.
 */
export interface CreateTaskFormHandle {
  /** Validate + submit. No-op when not submittable. Resets on success. */
  submit: () => Promise<void>;
  /** Clear all fields back to their initial state. */
  reset: () => void;
  /**
   * Snapshot of the current field values — used to build a shareable deep-link
   * of the in-progress form. Mirrors the {@link CreateTaskInitialValues} shape
   * so the link round-trips back through the prefill path. Title/description are
   * trimmed; the primary resource and attachments are intentionally omitted (not
   * expressible as link params).
   */
  getValues: () => CreateTaskInitialValues;
}

export interface CreateTaskFormProps {
  onCreateTask: (input: CreateTaskInput) => Promise<void>;
  /**
   * Selectable task templates (the same list {@link NewTaskSplitButton} shows).
   * When provided, the form gains template mode: a "Start from template" picker
   * on the blank form; with a template active, its merge-tag field inputs sit
   * above the normal title/description (which become derived + read-only,
   * re-rendering live as the fields are filled) and the template's defaults
   * populate the real attribute controls. The picker menu's top option is
   * "Blank task" (plain unselect). Switching — to another template or to blank
   * — with entered values prompts before discarding them. When omitted, the
   * form renders exactly as before.
   */
  templates?: TaskTemplate[];
  /** Template preselected on mount (e.g. picked via the New-task split button). */
  initialTemplate?: TaskTemplate;
  /** Assignable users and agents. When provided, the assignee selector is shown. */
  assigneeOptions?: TaskIdentitySummary[];
  /**
   * Pre-linked resource. When provided without `resourcePickerSlot`, shown
   * read-only. When `resourcePickerSlot` is also provided, used as the initial
   * selection.
   */
  primaryResource?: PrimaryResourceRef;
  /**
   * The parent task this task is being created as a sub-task of (e.g. from the
   * "Add sub-task" action on a task's detail page). Read-only in the form —
   * unlike `primaryResource` there is no picker; the relationship is fixed by
   * the entry point that opened the form. Rendered as a chip and always
   * included on submit as `parent_task_id`.
   */
  parentTask?: TaskParentRef | null;
  /**
   * Render prop for the resource picker. Receives the current selection and a
   * setter; should render a trigger+popover that lets the user pick or clear a
   * file. When omitted, a read-only chip is shown if `primaryResource` is set.
   */
  resourcePickerSlot?: (
    current: PrimaryResourceRef | null,
    onSelect: (ref: PrimaryResourceRef | null) => void,
  ) => ReactNode;
  /**
   * Node rendered in the attribute row for file attachments — typically the
   * shared task file picker (search workspace files or upload). The consumer
   * owns the attachment state and attaches files after the task is created.
   */
  attachmentSlot?: ReactNode;
  /**
   * Called with files dropped anywhere on the form body. Lets the consumer
   * upload + register them with the same attachment state the slot renders.
   */
  onDropFiles?: (files: File[]) => void;
  /**
   * Notifies the parent whenever the form's submittable state changes (a
   * non-empty title, or all required template fields filled). Lets the
   * surrounding chrome gate its own submit control.
   */
  onCanSubmitChange?: (canSubmit: boolean) => void;
  /**
   * Initial field values, applied on mount and re-applied by `reset()`. Used
   * for deep-link prefill (create mode) or the task being edited (edit mode).
   * Omitted fields fall back to their empty/default state. Read once (like
   * `primaryResource`) — the form owns state after mount.
   */
  initialValues?: CreateTaskInitialValues;
  /**
   * `"create"` (default) is the classic new-task form: the template picker is
   * available, `onCreateTask` is called with only the fields that differ from
   * their defaults (so a blank field means "use the default"), and a
   * successful submit resets the form back to `initialValues`.
   *
   * `"edit"` is for editing an existing task in place ({@link TaskEditPage}):
   * the template picker is hidden, `onCreateTask` is called with every field's
   * current value (so an explicit revert-to-default — e.g. clearing the
   * assignee — is sent rather than silently dropped), and the form is left as
   * the user last edited it after submit (the caller navigates away).
   */
  mode?: "create" | "edit";
}

function fieldInputId(template: TaskTemplate, field: TaskTemplateField): string {
  return `${template.publicId}-${field.key}`;
}

function unsetSelectValue(options: string[]): string {
  let value = "__unset__";
  while (options.includes(value)) value += "_";
  return value;
}

function templateDueDate(template: TaskTemplate): number | null {
  return template.defaults.dueOffset ? resolveDueOffset(template.defaults.dueOffset) : null;
}

/** The confirmation target for a pending mode change: a template, or blank. */
type PendingSwitch = TaskTemplate | "blank";

/**
 * The shared body of the "create task" experience — title, description, status,
 * due date, assignee, primary resource, and file attachments, plus drag-and-drop
 * upload. When `templates` is provided, the same form also covers template
 * selection and instantiation (states 1 and 4 of the task-templates
 * wireframes): the form keeps its exact dashboard shape, with the template's
 * merge-tag field inputs above a derived (read-only) title/description and the
 * template defaults prefilling the real attribute controls. "Edit as blank task
 * instead" unlocks the derived text for one-off tweaks. Rendered by
 * {@link CreateTaskDialog} (inside a modal), the full-page `/tasks/new` route,
 * and — via `mode="edit"` — the full-page `/tasks/:taskId/edit` route, so all
 * three stay identical by construction.
 */
export const CreateTaskForm = forwardRef<CreateTaskFormHandle, CreateTaskFormProps>(
  function CreateTaskForm(
    {
      onCreateTask,
      initialValues,
      templates,
      initialTemplate,
      assigneeOptions,
      primaryResource: primaryResourceProp,
      parentTask = null,
      resourcePickerSlot,
      attachmentSlot,
      onDropFiles,
      onCanSubmitChange,
      mode = "create",
    },
    ref,
  ) {
    const isEditMode = mode === "edit";
    const [title, setTitle] = useState(initialValues?.title ?? "");
    const [description, setDescription] = useState(initialValues?.description ?? "");
    // Template defaults populate the real controls at selection time; the user
    // can change them like normal afterwards.
    const [assignee, setAssignee] = useState<TaskIdentitySummary | null>(
      initialTemplate?.defaults.assignee ?? initialValues?.assignee ?? null,
    );
    const [status, setStatus] = useState<TaskStatus>(initialValues?.status ?? "triage");
    // A template's priority default populates the visible priority selector,
    // like the other template defaults. Kept across "edit as blank task".
    const [priority, setPriority] = useState<TaskPriority>(
      initialTemplate?.defaults.priority ?? initialValues?.priority ?? DEFAULT_TASK_PRIORITY,
    );
    const [dueDate, setDueDate] = useState<number | null>(() =>
      initialTemplate ? templateDueDate(initialTemplate) : (initialValues?.dueDate ?? null),
    );
    // `primaryResourceProp` is read on mount (and re-applied by reset()). The
    // dialog unmounts its content on close, so the form remounts per-open and
    // re-reads the prop — keep that invariant if the dialog ever gains
    // forceMount, or a kept-mounted caller (full page) will ignore prop changes.
    const [selectedResource, setSelectedResource] = useState<PrimaryResourceRef | null>(
      primaryResourceProp ?? null,
    );

    // Template mode. `activeTemplate === null` = blank task (the classic form).
    const [activeTemplate, setActiveTemplate] = useState<TaskTemplate | null>(
      initialTemplate ?? null,
    );
    const [fieldValues, setFieldValues] = useState<Record<string, string>>(() =>
      initialTemplate ? initialTemplateValues(initialTemplate) : {},
    );
    const pristineFieldValues = useRef(fieldValues);
    /** Mode change awaiting confirmation because entered values would be lost. */
    const [pendingSwitch, setPendingSwitch] = useState<PendingSwitch | null>(null);

    // Keep a selected template on the latest prop snapshot as async defaults resolve,
    // without replacing attribute values the user has changed.
    useEffect(() => {
      if (!templates) return;

      if (activeTemplate) {
        const refreshedTemplate = templates.find(
          (template) => template.publicId === activeTemplate.publicId,
        );
        if (refreshedTemplate && refreshedTemplate !== activeTemplate) {
          const previousAssigneeId = activeTemplate.defaults.assignee?.publicId;
          const previousPriority = activeTemplate.defaults.priority ?? DEFAULT_TASK_PRIORITY;
          const previousDueDate = templateDueDate(activeTemplate);
          setAssignee((current) =>
            current?.publicId === previousAssigneeId
              ? (refreshedTemplate.defaults.assignee ?? null)
              : current,
          );
          setPriority((current) =>
            current === previousPriority
              ? (refreshedTemplate.defaults.priority ?? DEFAULT_TASK_PRIORITY)
              : current,
          );
          setDueDate((current) =>
            current === previousDueDate ? templateDueDate(refreshedTemplate) : current,
          );
          setActiveTemplate(refreshedTemplate);
        }
      }

      if (pendingSwitch && pendingSwitch !== "blank") {
        const refreshedSwitch = templates.find(
          (template) => template.publicId === pendingSwitch.publicId,
        );
        if (refreshedSwitch && refreshedSwitch !== pendingSwitch) {
          setPendingSwitch(refreshedSwitch);
        }
      }
    }, [activeTemplate, pendingSwitch, templates]);

    // Drag-and-drop affordance — dropped files are forwarded to the consumer.
    const [isDragOver, setIsDragOver] = useState(false);
    const dragCounter = useRef(0);

    const setFieldValue = useCallback((key: string, value: string) => {
      setFieldValues((prev) => ({ ...prev, [key]: value }));
    }, []);

    // Dirty = whatever a template switch would discard. In template mode that
    // is any merge-field value differing from its initial value; on the blank
    // form it is any edit to title/description relative to the prefill. In both
    // modes it also covers the attribute values applyTemplate() overwrites —
    // assignee, priority and due date, compared against the current mode's
    // baseline. Status, resource and attachments survive a switch untouched,
    // so they do not count.
    const attributeBaseline = activeTemplate
      ? {
          assigneeId: activeTemplate.defaults.assignee?.publicId ?? null,
          priority: activeTemplate.defaults.priority ?? DEFAULT_TASK_PRIORITY,
          dueDate: templateDueDate(activeTemplate),
        }
      : {
          assigneeId: initialValues?.assignee?.publicId ?? null,
          priority: initialValues?.priority ?? DEFAULT_TASK_PRIORITY,
          dueDate: initialValues?.dueDate ?? null,
        };
    const attributeRowDirty =
      (assignee?.publicId ?? null) !== attributeBaseline.assigneeId ||
      priority !== attributeBaseline.priority ||
      dueDate !== attributeBaseline.dueDate;

    const isDirty =
      attributeRowDirty ||
      (activeTemplate
        ? activeTemplate.fields.some(
            (field) =>
              (fieldValues[field.key] ?? "") !== (pristineFieldValues.current[field.key] ?? ""),
          )
        : title !== (initialValues?.title ?? "") ||
          description !== (initialValues?.description ?? ""));

    /**
     * Switches to a template (its defaults become the new starting point) or,
     * for `null`, back to the blank form (the picker's "Blank task" option).
     */
    const applyTemplate = useCallback(
      (next: TaskTemplate | null) => {
        const nextFieldValues = next ? initialTemplateValues(next) : {};
        pristineFieldValues.current = nextFieldValues;
        setActiveTemplate(next);
        setFieldValues(nextFieldValues);
        setTitle(next ? "" : (initialValues?.title ?? ""));
        setDescription(next ? "" : (initialValues?.description ?? ""));
        setAssignee(next ? (next.defaults.assignee ?? null) : (initialValues?.assignee ?? null));
        setPriority(
          next
            ? (next.defaults.priority ?? DEFAULT_TASK_PRIORITY)
            : (initialValues?.priority ?? DEFAULT_TASK_PRIORITY),
        );
        setDueDate(next ? templateDueDate(next) : (initialValues?.dueDate ?? null));
        setPendingSwitch(null);
      },
      [initialValues],
    );

    const applySwitch = useCallback(
      (target: PendingSwitch) => applyTemplate(target === "blank" ? null : target),
      [applyTemplate],
    );

    const requestSwitch = useCallback(
      (target: PendingSwitch) => {
        if (target === "blank" ? !activeTemplate : target.publicId === activeTemplate?.publicId) {
          return;
        }
        if (isDirty) {
          setPendingSwitch(target);
        } else {
          applySwitch(target);
        }
      },
      [activeTemplate, isDirty, applySwitch],
    );

    /**
     * "Edit as blank task instead" — unlock the derived text: keep the rendered
     * title/description (and the populated attribute controls) as a plain
     * editable blank task. Distinct from the picker's "Blank task" option,
     * which is a plain unselect back to an empty form.
     */
    const editAsBlankTask = useCallback(() => {
      if (!activeTemplate) return;
      setTitle(renderTemplateString(activeTemplate.titleTemplate, fieldValues));
      setDescription(renderTemplateString(activeTemplate.descriptionTemplate, fieldValues));
      setActiveTemplate(null);
      setFieldValues({});
    }, [activeTemplate, fieldValues]);

    // In template mode the title/description are derived from the template and
    // the entered field values; the inputs render these read-only. The title's
    // segments are kept (not just the joined string) so submit can be gated on
    // unresolved `{{tags}}`.
    const renderedTitleSegments = useMemo(
      () =>
        activeTemplate ? renderTemplateSegments(activeTemplate.titleTemplate, fieldValues) : null,
      [activeTemplate, fieldValues],
    );
    const renderedTitle = useMemo(
      () =>
        renderedTitleSegments
          ? renderedTitleSegments.map((segment) => segment.text).join("")
          : null,
      [renderedTitleSegments],
    );
    const renderedDescription = useMemo(
      () =>
        activeTemplate
          ? renderTemplateString(activeTemplate.descriptionTemplate, fieldValues)
          : null,
      [activeTemplate, fieldValues],
    );

    // In template mode, gate on the required fields, a non-empty derived
    // title, AND no unresolved `{{tags}}` left in the title — an unfilled
    // *optional* tag renders verbatim (deliberate for descriptions, where
    // nothing is silently dropped), but a task titled "Review {{customer}}"
    // shouldn't ship. "Edit as blank task instead" remains the escape hatch.
    const canSubmit = activeTemplate
      ? (renderedTitle ?? "").trim().length > 0 &&
        (renderedTitleSegments ?? []).every((segment) => segment.kind !== "tag") &&
        activeTemplate.fields.every(
          (field) => !field.required || (fieldValues[field.key]?.trim().length ?? 0) > 0,
        )
      : title.trim().length > 0;
    useEffect(() => {
      onCanSubmitChange?.(canSubmit);
    }, [canSubmit, onCanSubmitChange]);

    const reset = useCallback(() => {
      setTitle(initialValues?.title ?? "");
      setDescription(initialValues?.description ?? "");
      setAssignee(initialTemplate?.defaults.assignee ?? initialValues?.assignee ?? null);
      setStatus(initialValues?.status ?? "triage");
      setPriority(
        initialTemplate?.defaults.priority ?? initialValues?.priority ?? DEFAULT_TASK_PRIORITY,
      );
      setDueDate(
        initialTemplate ? templateDueDate(initialTemplate) : (initialValues?.dueDate ?? null),
      );
      setSelectedResource(primaryResourceProp ?? null);
      setActiveTemplate(initialTemplate ?? null);
      const resetFieldValues = initialTemplate ? initialTemplateValues(initialTemplate) : {};
      pristineFieldValues.current = resetFieldValues;
      setFieldValues(resetFieldValues);
      setPendingSwitch(null);
      dragCounter.current = 0;
      setIsDragOver(false);
    }, [initialValues, primaryResourceProp, initialTemplate]);

    const submit = useCallback(async () => {
      if (!canSubmit) return;

      const resolvedTitle = (renderedTitle ?? title).trim();
      if (!resolvedTitle) return;
      const resolvedDescription = (renderedDescription ?? description).trim();

      if (isEditMode) {
        // Every field's current value is sent explicitly — unlike create mode,
        // a field back at its "default" (e.g. status reverted to triage, or the
        // assignee cleared) is a real edit that must reach the caller, not get
        // silently dropped by an omission the create-mode gate below relies on.
        await onCreateTask({
          title: resolvedTitle,
          description: resolvedDescription,
          assignee: assignee ?? null,
          status,
          priority,
          dueDate,
          ...(selectedResource ? { primaryResource: selectedResource } : {}),
          ...(parentTask ? { parentTask } : {}),
        });
        // No reset(): the caller (TaskEditPage) navigates away on success, and
        // resetting here would momentarily snap the form back to the
        // pre-edit values it was seeded with.
        return;
      }

      await onCreateTask({
        title: resolvedTitle,
        ...(resolvedDescription ? { description: resolvedDescription } : {}),
        ...(assignee ? { assignee } : {}),
        ...(status !== "triage" ? { status } : {}),
        ...(priority !== DEFAULT_TASK_PRIORITY ? { priority } : {}),
        ...(dueDate != null ? { dueDate } : {}),
        ...(selectedResource ? { primaryResource: selectedResource } : {}),
        ...(parentTask ? { parentTask } : {}),
      });

      reset();
    }, [
      canSubmit,
      renderedTitle,
      renderedDescription,
      title,
      description,
      assignee,
      status,
      priority,
      dueDate,
      selectedResource,
      parentTask,
      onCreateTask,
      reset,
      isEditMode,
    ]);

    const getValues = useCallback((): CreateTaskInitialValues => {
      // Mirror submit: in template mode title/description state is blank and the
      // visible text is derived — snapshot the rendered values so Copy link
      // round-trips what the user sees.
      const resolvedTitle = (renderedTitle ?? title).trim();
      const resolvedDescription = (renderedDescription ?? description).trim();
      return {
        ...(resolvedTitle ? { title: resolvedTitle } : {}),
        ...(resolvedDescription ? { description: resolvedDescription } : {}),
        ...(assignee ? { assignee } : {}),
        status,
        priority,
        ...(dueDate != null ? { dueDate } : {}),
      };
    }, [
      renderedTitle,
      renderedDescription,
      title,
      description,
      assignee,
      status,
      priority,
      dueDate,
    ]);

    useImperativeHandle(ref, () => ({ submit, reset, getValues }), [submit, reset, getValues]);

    const showPicker = !isEditMode && (templates?.length ?? 0) > 0;

    // Drag-and-drop handlers (counter pattern handles nested elements)
    function handleDragEnter(e: DragEvent<HTMLDivElement>) {
      if (!onDropFiles) return;
      if (!e.dataTransfer.types.includes("Files")) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current += 1;
      setIsDragOver(true);
    }
    function handleDragLeave(e: DragEvent<HTMLDivElement>) {
      if (!onDropFiles) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = Math.max(0, dragCounter.current - 1);
      if (dragCounter.current === 0) setIsDragOver(false);
    }
    function handleDragOver(e: DragEvent<HTMLDivElement>) {
      if (!onDropFiles) return;
      if (!e.dataTransfer.types.includes("Files")) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "copy";
    }
    function handleDrop(e: DragEvent<HTMLDivElement>) {
      if (!onDropFiles) return;
      if (!e.dataTransfer.types.includes("Files")) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragOver(false);
      void filesFromDataTransfer(e.dataTransfer).then((files) => {
        if (files.length > 0) {
          onDropFiles(files);
        } else {
          toast.error("No uploadable files found in the drop.");
        }
      });
    }

    return (
      <div
        className={cn(
          "grid gap-3 rounded-md transition-colors",
          isDragOver && "outline outline-2 outline-offset-2 outline-primary/40",
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Template picker — "Start from template" on the blank form, the
            active template name (as a "Change template" control) otherwise.
            "Blank task" is the top option: a plain unselect. */}
        {showPicker && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                className={cn(
                  "-ml-2 h-auto w-fit gap-1.5 px-2 py-1",
                  activeTemplate
                    ? "text-base font-semibold text-foreground"
                    : "text-sm font-normal text-muted-foreground",
                )}
                aria-label={activeTemplate ? "Change template" : "Start from template"}
              >
                {activeTemplate ? activeTemplate.name : "Start from template"}
                <ChevronDown className="size-4 text-muted-foreground" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem
                onClick={() => requestSwitch("blank")}
                className={cn(!activeTemplate && "bg-muted/60")}
              >
                Blank task
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {(templates ?? []).map((option) => (
                <DropdownMenuItem
                  key={option.publicId}
                  onClick={() => requestSwitch(option)}
                  className={cn(
                    "justify-between gap-4",
                    option.publicId === activeTemplate?.publicId && "bg-muted/60",
                  )}
                >
                  <span className="truncate">{option.name}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {templateFieldCountLabel(option)}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Parent task — read-only, set by the entry point (e.g. "Add sub-task"). */}
        {parentTask && (
          <div className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
            <span>Sub-task of</span>
            <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-0.5 font-medium text-foreground">
              <span className="shrink-0 rounded-sm bg-background px-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                TASK-{parentTask.taskNumber ?? "?"}
              </span>
              <span className="max-w-[220px] truncate">{parentTask.title}</span>
            </span>
          </div>
        )}

        {/* Merge-tag field inputs, generated from the active template. The
            title/description below re-render live as these are filled. */}
        {activeTemplate?.fields.map((field) => (
          <div key={field.key} className="grid gap-1.5">
            <Label htmlFor={fieldInputId(activeTemplate, field)} required={field.required}>
              {field.label}
            </Label>
            <TemplateFieldInput
              id={fieldInputId(activeTemplate, field)}
              field={field}
              value={fieldValues[field.key] ?? ""}
              onChange={(value) => setFieldValue(field.key, value)}
            />
          </div>
        ))}

        {/* Same chrome-less title/description as the task detail page. With a
            template active they are derived (read-only); "Edit as blank task
            instead" unlocks them. */}
        <TaskTitleInput
          value={renderedTitle ?? title}
          onChange={setTitle}
          placeholder="Task title"
          readOnly={activeTemplate !== null}
          autoFocus={!activeTemplate}
          className={cn(activeTemplate && "bg-muted/40 text-muted-foreground")}
        />
        <MarkdownEditor
          variant="seamless"
          value={renderedDescription ?? description}
          onChange={setDescription}
          placeholder="Add context, expected outcome, or type @ to mention"
          aria-label="Task description"
          editable={activeTemplate === null}
          {...(assigneeOptions
            ? { mentionOptions: mentionablesFromIdentities(assigneeOptions) }
            : {})}
          className={cn(
            "min-h-[120px] px-1",
            activeTemplate && "rounded-md bg-muted/40 text-muted-foreground",
          )}
        />

        {activeTemplate && (
          <div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-1.5 px-2 text-muted-foreground"
              onClick={editAsBlankTask}
            >
              <PencilLine className="size-3.5" aria-hidden />
              Edit as blank task instead
            </Button>
          </div>
        )}

        {/* Single-row attribute bar */}
        <div className="flex flex-wrap items-center gap-1 pt-0.5">
          <StatusSelector value={status} onSelect={setStatus} />
          <PrioritySelector value={priority} onSelect={setPriority} />
          <DueDateSelector value={dueDate} onSelect={setDueDate} />
          {assigneeOptions && assigneeOptions.length > 0 && (
            <AssigneeSelector options={assigneeOptions} value={assignee} onSelect={setAssignee} />
          )}

          <div className="ml-auto flex shrink-0 items-center gap-1">
            {/* Primary resource — delegated to consumer via render prop */}
            {resourcePickerSlot ? (
              resourcePickerSlot(selectedResource, setSelectedResource)
            ) : selectedResource ? (
              /* Read-only chip when no picker provided */
              <div className="flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-0.5 text-[12px]">
                <Link2 className="size-3 shrink-0 text-muted-foreground" aria-hidden />
                <span className="shrink-0 rounded-sm bg-background px-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {selectedResource.type}
                </span>
                <span className="max-w-[140px] truncate font-medium text-foreground">
                  {selectedResource.label}
                </span>
              </div>
            ) : null}

            {/* Attachments — delegated to consumer (shared task file picker) */}
            {attachmentSlot}
          </div>
        </div>

        {/* Drag-over hint */}
        {isDragOver && (
          <div className="flex items-center justify-center gap-2 rounded-md border-2 border-dashed border-primary/40 bg-primary/5 py-3 text-sm text-muted-foreground">
            <Paperclip className="size-4" aria-hidden />
            Drop files to attach
          </div>
        )}

        {/* Confirmation before a dirty switch discards entered values */}
        <AlertDialog
          open={pendingSwitch !== null}
          onOpenChange={(open) => {
            if (!open) setPendingSwitch(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {pendingSwitch === "blank" ? "Remove template?" : "Switch template?"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {pendingSwitch === "blank"
                  ? "Your entered values will be discarded."
                  : pendingSwitch
                    ? `Your entered values will be discarded and replaced with the defaults for "${pendingSwitch.name}".`
                    : ""}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (pendingSwitch) applySwitch(pendingSwitch);
                }}
              >
                {pendingSwitch === "blank" ? "Remove template" : "Switch template"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  },
);

function TemplateFieldInput({
  id,
  field,
  value,
  onChange,
}: {
  id: string;
  field: TaskTemplateField;
  value: string;
  onChange: (value: string) => void;
}): ReactNode {
  switch (field.type) {
    case "textarea":
      return (
        <Textarea
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="min-h-[80px] resize-none"
        />
      );
    case "date":
      return (
        <Input
          id={id}
          type="date"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case "select": {
      const options = field.options ?? [];
      const unsetValue = unsetSelectValue(options);
      return (
        <Select
          value={value}
          onValueChange={(nextValue) => onChange(nextValue === unsetValue ? "" : nextValue)}
        >
          <SelectTrigger id={id} className="w-full">
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent>
            {!field.required && <SelectItem value={unsetValue}>None</SelectItem>}
            {options.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    case "text":
      return <Input id={id} value={value} onChange={(event) => onChange(event.target.value)} />;
  }
}
